import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import Button from '../common/Button';
import FormInput from '../components/form/FormInput';
import { useResetPassword } from '../hooks/tanstack/useAuthHook';
import routes from '../routes';
import { handleQueryError } from '../utils/api';
import cn from '../utils/cn';

const PasswordResetFormSchema = z
  .object({
    password: z
      .string()
      .trim()
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: z
      .string()
      .trim()
      .min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Confirm password does not match',
    path: ['confirmPassword'],
  });

type PasswordResetFormSchemaType = z.infer<typeof PasswordResetFormSchema>;

const PasswordResetForm = () => {
  const [error, setError] = useState(Object);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAllPassword, setShowAllPassword] = useState(false);

  const token = searchParams.get('token');

  const navigate = useNavigate();

  const { mutate: resetPassword, isPending } = useResetPassword({
    onSuccess: () => {
      navigate(routes.login);
    },
    onError: (error) => {
      handleQueryError(error, setError);
    },
  });

  const form = useForm<PasswordResetFormSchemaType>({
    resolver: zodResolver(PasswordResetFormSchema),
  });

  const { handleSubmit } = form;

  const onSubmit: SubmitHandler<PasswordResetFormSchemaType> = (data) => {
    if (!token) {
      return;
    }

    resetPassword({
      data: {
        token,
        new_password: data.confirmPassword,
      },
    });
  };

  const handleCTAOnClick = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('type');
    setSearchParams(newSearchParams);
  };

  const handleClickShowAllPasswords = () => {
    setShowAllPassword((show) => !show);
  };

  const currentYear = new Date().getFullYear();

  return (
    <div
      className={cn(
        'space-y-4 bg-primary-dark-blue flex items-center justify-center bg-cover px-[40px] pt-7 h-screen 3xl:overflow-hidden',
        {
          'translate-y-0': searchParams.get('type') === 'reset_password',
        }
      )}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="border-grey-500 text-white flex h-fit w-full max-w-[600px] flex-col items-start gap-3 rounded-xl border-[2px] px-8 py-6 backdrop-blur-sm"
        >
          <div className="flex flex-col pb-6">
            <h1 className="text-2xl font-bold uppercase">
              Create new Password
            </h1>
            <p className="text-sm">
              Your new password must be different from previous used passwords.
            </p>
          </div>
          <FormInput
            name="password"
            label="Password"
            type="password"
            helperText="Must be at least 8 characters"
            helperTextStyle={{
              color: '#DCEBFE',
            }}
            showAllPassword={showAllPassword}
            handleShowAllPasswords={handleClickShowAllPasswords}
            labelClassName="text-white"
            style={{
              backgroundColor: 'white',
            }}
          />
          <FormInput
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            showAllPassword={showAllPassword}
            handleShowAllPasswords={handleClickShowAllPasswords}
            labelClassName="text-white"
            helperText="Both password must match"
            helperTextStyle={{
              color: '#DCEBFE',
            }}
            style={{
              backgroundColor: 'white',
            }}
          />
          <Button loading={isPending} type="submit" variant="contained_red">
            Submit
          </Button>
          <button
            type="button"
            onClick={handleCTAOnClick}
            className="w-full py-5 text-center"
          >
            <p>Back to Login</p>
          </button>
          <div className="w-full text-center font-light">
            <p>@{currentYear} ISBE Group</p>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default PasswordResetForm;
